import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AumicoCoreProvider } from '@modeso/aumico-lib-core-fe';
import { IMultiYearComparisonResponseDTO } from '@modeso/types__aumico-lib-annualreport-be';
import { ICompanyDTO, IMycCompanyPreferences } from '@modeso/types__aumico-lib-companies-be';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AumicoCompaniesConfig, AumicoCompaniesConfigService } from '../aumico-companies.config';

/**
 * HTTP client for the Mehrjahresvergleich (multi-year comparison) endpoint.
 *
 * The annualreport microservice owns the data and the harmonization logic,
 * so we hit that service here even though the UX lives under "companies".
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    private readonly server: string;
    private readonly annualReportMicroservice = 'annualreport';

    constructor(
        private readonly http: HttpClient,
        @Inject(AumicoCompaniesConfigService) private readonly config: AumicoCompaniesConfig,
        private readonly coreProvider: AumicoCoreProvider,
    ) {
        this.server = config.apiGateway;
    }

    /**
     * Fetch the multi-year comparison harmonized DTO.
     *
     * Pass `language` to pin the result to a specific AR locale. When the
     * company has reports in multiple locales AND `language` is omitted, the
     * BE responds with HTTP 409 + `{ code: 'LANGUAGE_MISMATCH',
     * availableLanguages }` — the effect catches that and triggers the
     * language-selection modal.
     */
    public getMultiYearComparison$(
        companyId: string,
        language?: string,
    ): Observable<IMultiYearComparisonResponseDTO> {
        const url = `${this.server}${this.annualReportMicroservice}/multi-year-comparison/${companyId}`;
        const params = language ? new HttpParams().set('language', language) : undefined;
        const requestKey = 'getMultiYearComparison$';
        this.coreProvider.dispatchBlockingRequest(requestKey);
        return this.http
            .get<IMultiYearComparisonResponseDTO>(url, { params })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd(requestKey)));
    }

    /**
     * Partial update of `Company.mycPreferences` — persists Mehrjahresvergleich
     * settings (controls-bar + export-modal). `partial` only carries the
     * fields that changed; the BE does `$set` so unrelated fields stay intact.
     *
     * Concurrent writes on different fields don't collide (different `$set`
     * paths); concurrent writes on the same field follow last-write-wins,
     * which is fine for soft UI state.
     *
     * Response: the full updated `ICompanyDTO`. Callers may ignore it (we
     * already have the in-flight optimistic state in NgRx).
     */
    public patchMycPreferences$(
        companyId: string,
        partial: IMycCompanyPreferences,
    ): Observable<ICompanyDTO> {
        const url = `${this.server}companies/${companyId}/myc-preferences`;
        // Intentionally NOT calling `coreProvider.dispatchBlockingRequest` —
        // these writes are background side-effects of UI interactions; we
        // don't want a global blocker / spinner to fire on every toggle.
        return this.http.patch<ICompanyDTO>(url, { preferences: partial });
    }

    /**
     * POST the FE-built multi-year PDFTron payload to aumico-be.
     *
     * Mirrors the V2 PDF endpoint pattern (`POST /reporting/pdf/v2`):
     * returns 202 immediately; the actual PDF arrives via WebSocket
     * (`NotificationsSocketService.printJobCompleted$`).
     */
    public requestMultiYearPdf$(payload: object): Observable<void> {
        const url = `${this.server}reporting/multiyear/pdf`;
        const requestKey = 'requestMultiYearPdf$';
        this.coreProvider.dispatchBlockingRequest(requestKey);
        return this.http
            .post<void>(url, { data: payload })
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd(requestKey)));
    }
}
